import { Search, Coins, History, Ban, CheckCircle, Pencil, Lock, LogIn, AlertTriangle, CreditCard, ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationInfo } from '../../services/api';
import type { UserData } from './types';
import { formatBeijingDate, formatInBeijing } from '../../utils/time';

interface UsersTabProps {
  users: UserData[];
  pagination: PaginationInfo;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  multiIpOnly?: boolean;
  onMultiIpOnlyChange?: (value: boolean) => void;
  minSameIp?: number;
  onMinSameIpChange?: (value: number) => void;
  multiLoginIpOnly?: boolean;
  onMultiLoginIpOnlyChange?: (value: boolean) => void;
  minSameLoginIp?: number;
  onMinSameLoginIpChange?: (value: number) => void;
  sameFingerprintOnly?: boolean;
  onSameFingerprintOnlyChange?: (value: boolean) => void;
  onAdjustCredits: (user: UserData) => void;
  onViewCreditHistory: (user: UserData) => void;
  onViewRechargeOrders: (user: UserData) => void;
  onToggleUser: (userId: string) => void;
  onEditUser: (user: UserData) => void;
  onResetPassword: (user: UserData) => void;
  onImpersonateUser: (user: UserData) => void;
  onPageChange: (page: number) => void;
}

export default function UsersTab({
  users,
  pagination,
  loading,
  searchQuery,
  onSearchChange,
  multiIpOnly = false,
  onMultiIpOnlyChange,
  minSameIp = 2,
  onMinSameIpChange,
  multiLoginIpOnly = false,
  onMultiLoginIpOnlyChange,
  minSameLoginIp = 2,
  onMinSameLoginIpChange,
  sameFingerprintOnly = false,
  onSameFingerprintOnlyChange,
  onAdjustCredits,
  onViewCreditHistory,
  onViewRechargeOrders,
  onToggleUser,
  onEditUser,
  onResetPassword,
  onImpersonateUser,
  onPageChange,
}: UsersTabProps) {
  const displayUsers = users;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
      <div className="p-4 border-b border-light-border space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
          <input
            type="text"
            placeholder="搜索邮箱、昵称或注册 IP..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue"
          />
        </div>
        {onMultiIpOnlyChange && (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-light-text">
              <input
                type="checkbox"
                checked={multiIpOnly}
                onChange={(e) => onMultiIpOnlyChange(e.target.checked)}
                className="w-4 h-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
              />
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span>仅显示同 IP 注册多账号</span>
            </label>
            {multiIpOnly && (
              <div className="flex items-center gap-2 text-sm text-light-muted">
                <span>同 IP 账号数 ≥</span>
                <input
                  type="number"
                  min={2}
                  value={minSameIp}
                  onChange={(e) => onMinSameIpChange?.(Math.max(2, parseInt(e.target.value) || 2))}
                  className="w-16 px-2 py-1 border border-light-border rounded-lg focus:outline-none focus:border-accent-blue"
                />
              </div>
            )}
            {onMultiLoginIpOnlyChange && (
              <>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-light-text">
                  <input
                    type="checkbox"
                    checked={multiLoginIpOnly}
                    onChange={(e) => onMultiLoginIpOnlyChange(e.target.checked)}
                    className="w-4 h-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                  />
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  <span>仅显示同 IP 登录多账号</span>
                </label>
                {multiLoginIpOnly && (
                  <div className="flex items-center gap-2 text-sm text-light-muted">
                    <span>同 IP 账号数 ≥</span>
                    <input
                      type="number"
                      min={2}
                      value={minSameLoginIp}
                      onChange={(e) => onMinSameLoginIpChange?.(Math.max(2, parseInt(e.target.value) || 2))}
                      className="w-16 px-2 py-1 border border-light-border rounded-lg focus:outline-none focus:border-accent-blue"
                    />
                  </div>
                )}
              </>
            )}
            {onSameFingerprintOnlyChange && (
              <label className="flex items-center gap-2 cursor-pointer text-sm text-light-text">
                <input
                  type="checkbox"
                  checked={sameFingerprintOnly}
                  onChange={(e) => onSameFingerprintOnlyChange(e.target.checked)}
                  className="w-4 h-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                />
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <span>仅显示同设备指纹多账号</span>
              </label>
            )}
          </div>
        )}
      </div>

      {searchQuery && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-600">
            搜索 "{searchQuery}" 找到 {pagination.total} 个用户
          </p>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-light-hover">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">用户</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">注册时间</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">积分</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">图片数</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">最后登录时间</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">最后登录IP</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">注册IP</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">设备指纹</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">状态</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-light-text">操作</th>
            </tr>
          </thead>
          <tbody>
            {displayUsers.length === 0 ? (
                <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-light-muted">
                  {searchQuery ? '没有找到匹配的用户' : '暂无用户数据'}
                </td>
              </tr>
            ) : (
              displayUsers.map((user) => (
                <tr key={user.id} className="border-t border-light-border">
                  <td className="px-4 py-3 w-[280px] max-w-[280px]">
                    <div className="min-w-0">
                      <p
                        className="truncate text-sm font-medium text-light-text"
                        title={user.email}
                      >
                        {user.email}
                      </p>
                      <p
                        className="truncate text-xs text-light-muted"
                        title={user.name || '未设置昵称'}
                      >
                        {user.name || '未设置昵称'}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-text">
                      {formatInBeijing(user.createdAt, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-text">{user.credits}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-text">{user.imageCount}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-light-text">
                      {user.lastLoginAt
                        ? formatInBeijing(user.lastLoginAt, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '从未登录'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-light-text font-mono">
                        {user.lastLoginIp || '-'}
                      </span>
                      {user.sameLoginIpCount && user.sameLoginIpCount > 1 ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700 text-xs font-medium"
                          title={`该登录 IP 共关联 ${user.sameLoginIpCount} 个账号`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          ×{user.sameLoginIpCount}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-light-text font-mono">
                        {user.registerIp || '-'}
                      </span>
                      {user.sameIpCount && user.sameIpCount > 1 ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700 text-xs font-medium"
                          title={`该 IP 共注册了 ${user.sameIpCount} 个账号`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          ×{user.sameIpCount}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-light-text font-mono">
                        {user.deviceFingerprint || '-'}
                      </span>
                      {user.sameFingerprintCount && user.sameFingerprintCount > 1 ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700 text-xs font-medium"
                          title={`该设备指纹共关联 ${user.sameFingerprintCount} 个账号`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          ×{user.sameFingerprintCount}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex whitespace-nowrap px-2 py-1 rounded-lg text-xs ${
                      user.isActive
                        ? 'bg-green-100 text-green-600'
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {user.isActive ? '正常' : '禁用'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => onAdjustCredits(user)}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        title="调整积分"
                      >
                        <Coins className="w-4 h-4" />
                        <span>积分调整</span>
                      </button>
                      <button
                        onClick={() => onViewCreditHistory(user)}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                        title="查看积分记录"
                      >
                        <History className="w-4 h-4" />
                        <span>积分记录</span>
                      </button>
                      <button
                        onClick={() => onViewRechargeOrders(user)}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                        title="查看充值订单"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>充值记录</span>
                      </button>
                      <button
                        onClick={() => onEditUser(user)}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                        title="编辑用户"
                      >
                        <Pencil className="w-4 h-4" />
                        <span>编辑</span>
                      </button>
                      <button
                        onClick={() => onResetPassword(user)}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                        title="重置密码"
                      >
                        <Lock className="w-4 h-4" />
                        <span>重置密码</span>
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => onImpersonateUser(user)}
                          className="inline-flex items-center gap-1 px-3 py-2 text-sm text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
                          title="以该用户身份打开新标签页"
                        >
                          <LogIn className="w-4 h-4" />
                          <span>登录</span>
                        </button>
                      )}
                      <button
                        onClick={() => onToggleUser(user.id)}
                        className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                          user.isActive
                            ? 'text-red-600 bg-red-50 hover:bg-red-100'
                            : 'text-green-600 bg-green-50 hover:bg-green-100'
                        }`}
                        title={user.isActive ? '禁用用户' : '启用用户'}
                      >
                        {user.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        <span>{user.isActive ? '禁用' : '启用'}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        {displayUsers.length === 0 ? (
          <div className="px-4 py-8 text-center text-light-muted">
            {searchQuery ? '没有找到匹配的用户' : '暂无用户数据'}
          </div>
        ) : (
          <div className="divide-y divide-light-border">
            {displayUsers.map((user) => (
              <div key={user.id} className="p-4">
                {/* User Info Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-light-text truncate">{user.email}</p>
                    <p className="text-xs text-light-muted">{user.name || '未设置昵称'}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-lg text-xs shrink-0 ml-2 ${
                    user.isActive
                      ? 'bg-green-100 text-green-600'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {user.isActive ? '正常' : '禁用'}
                  </span>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">积分</p>
                    <p className="text-sm font-medium text-light-text">{user.credits}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">图片</p>
                    <p className="text-sm font-medium text-light-text">{user.imageCount}</p>
                  </div>
                  <div className="bg-light-hover rounded-lg p-2 text-center">
                    <p className="text-xs text-light-muted">登录</p>
                    <p className="text-xs text-light-text">
                      {user.lastLoginAt
                        ? formatBeijingDate(user.lastLoginAt)
                        : '从未'}
                    </p>
                  </div>
                </div>

                {/* IP Address */}
                <div className="space-y-1 mb-3">
                  <p className="text-xs text-light-muted">
                    注册时间: <span className="text-light-text">{formatInBeijing(user.createdAt, {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}</span>
                  </p>
                  <p className="text-xs text-light-muted">
                    登录 IP: <span className="font-mono">{user.lastLoginIp || '-'}</span>
                  </p>
                  <p className="text-xs text-light-muted flex items-center gap-2 flex-wrap">
                    <span>注册 IP: <span className="font-mono">{user.registerIp || '-'}</span></span>
                    {user.sameIpCount && user.sameIpCount > 1 ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-100 text-orange-700 text-xs font-medium">
                        <AlertTriangle className="w-3 h-3" />
                        ×{user.sameIpCount}
                      </span>
                    ) : null}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => onAdjustCredits(user)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Coins className="w-4 h-4" />
                    积分调整
                  </button>
                  <button
                    onClick={() => onViewCreditHistory(user)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <History className="w-4 h-4" />
                    积分记录
                  </button>
                  <button
                    onClick={() => onViewRechargeOrders(user)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    充值记录
                  </button>
                  <button
                    onClick={() => onEditUser(user)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    编辑
                  </button>
                  <button
                    onClick={() => onResetPassword(user)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                  >
                    <Lock className="w-4 h-4" />
                    重置密码
                  </button>
                  {user.role !== 'admin' && (
                    <button
                      onClick={() => onImpersonateUser(user)}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors"
                    >
                      <LogIn className="w-4 h-4" />
                      登录
                    </button>
                  )}
                  <button
                    onClick={() => onToggleUser(user.id)}
                    className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                      user.isActive
                        ? 'text-red-600 bg-red-50 hover:bg-red-100'
                        : 'text-green-600 bg-green-50 hover:bg-green-100'
                    }`}
                  >
                    {user.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                    {user.isActive ? '禁用' : '启用'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t border-light-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-center text-sm text-light-muted sm:text-left">
            第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.total} 条
          </p>
          <div className="flex items-center justify-center gap-2 sm:justify-end">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1 || loading}
              className="inline-flex items-center gap-1 rounded-lg border border-light-border px-3 py-2 text-sm text-light-text transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">上一页</span>
            </button>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="inline-flex items-center gap-1 rounded-lg border border-light-border px-3 py-2 text-sm text-light-text transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="hidden sm:inline">下一页</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
